<template>
  <div>
    <PageHeader title="apollo-RP - 固定添付マスタ" class="head">
      <template #title-header-content>
        <div class="mr-auto ml-5 mt-auto mb-2 text-white text-sm">
          カテゴリーごとに、承認後のお客様宛メールへ初期値として付ける PDF です。
          <span class="ml-3 px-2 py-0.5 rounded bg-yellow-300 text-yellow-900 font-bold">管理者権限のみ編集可能</span>
        </div>
      </template>
    </PageHeader>

    <main class="main">
      <div v-if="loading" class="text-center text-gray-500 font-bold animate-pulse py-10">読み込み中…</div>

      <div v-else>
        <div class="text-sm text-gray-700 mb-3">見積の明細に種別が入ると、その種別の PDF が上から順にお客様宛メールの添付の初期値になります。見積ごとに外す・並べ替えができます。</div>
        <div class="kinds">
        <section v-for="k in kinds" :key="k.name" class="kind">
          <h2 class="kind-title">{{ k.name }}</h2>

          <table v-if="rowsOf(k.name).length" class="rows">
            <colgroup>
              <col class="col-no">
              <col>
              <col class="col-size">
              <col class="col-user">
              <col v-if="canSort(k.name)" class="col-arrow">
              <col v-if="canEdit" class="col-remove">
            </colgroup>
            <thead>
              <tr>
                <th class="text-center">順</th>
                <th>ファイル名</th>
                <th class="text-right">大きさ</th>
                <th>登録</th>
                <th v-if="canSort(k.name)"></th>
                <th v-if="canEdit"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in rowsOf(k.name)" :key="r.id">
                <td class="text-center">{{ i + 1 }}</td>
                <td class="file-name"><a class="text-indigo-700 underline cursor-pointer" @click="openFile(r)">{{ r.fileName }}</a></td>
                <td class="text-right whitespace-nowrap">{{ sizeText(r.fileSize) }}</td>
                <td class="text-xs text-gray-600 whitespace-nowrap">{{ r.updateDate }}</td>
                <td v-if="canSort(k.name)" class="text-center whitespace-nowrap">
                  <button type="button" class="arrow" :class="i > 0 ? '' : 'invisible'" :disabled="busy" @click="move(k.name, i, -1)"><Icon iconName="ArrowUp" class="h-4 w-4" /></button>
                  <button type="button" class="arrow" :class="i < rowsOf(k.name).length - 1 ? '' : 'invisible'" :disabled="busy" @click="move(k.name, i, 1)"><Icon iconName="ArrowDown" class="h-4 w-4" /></button>
                </td>
                <td v-if="canEdit" class="text-center">
                  <button type="button" class="remove" :disabled="busy" @click="remove(r)">削除</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-sm text-gray-500 py-3">PDF はまだありません。この種別の見積には何も付きません。</div>

          <!-- 追加：ドロップ枠（label なので押してもファイルを選べる。中の要素をまたぐたびに dragenter／dragleave が飛ぶので、中は pointer-events: none） -->
          <label
            v-if="canEdit"
            class="doc-drop"
            :class="[dragging == k.name ? 'is-dragging' : '', busy ? 'is-off' : '']"
            @dragenter.prevent="onDragEnter(k.name, $event)"
            @dragover.prevent
            @dragleave="onDragLeave()"
            @drop.prevent="onDrop(k.name, $event)"
          >
            <input type="file" accept=".pdf,application/pdf" class="hidden" multiple :disabled="busy" @change="pick(k.name, $event)" />
            <Icon iconName="DocumentAdd" iconType="outline" :strokeWidth="2" class="w-8 h-8 doc-drop-icon" />
            <div>
              <div class="doc-drop-main">{{ busy ? '保存中…' : dragging == k.name ? 'ここに落とすと追加します' : 'ここに PDF をドラッグ＆ドロップ' }}</div>
              <div v-if="!busy" class="doc-drop-sub">クリックするとフォルダから選択可能（複数可）。並びの最後に入ります</div>
            </div>
          </label>
        </section>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import PageHeader from '@components/PageHeader.vue'
import Icon from '@components/Icon.vue'
import * as estimateFixedAttachmentManager from '@managers/estimateFixedAttachmentManager'
import * as dialogs from '@libs/dialogs'
import * as logManager from '@managers/logManager'
import PageModel from '../../main/pages/PageModel.vue'

/**
 * ドラッグしているものにファイルが含まれるか（文字のドラッグなどは受けない）
 */
function hasFiles(event) {
  const types = event.dataTransfer ? Array.from(event.dataTransfer.types || []) : []
  return types.includes('Files')
}

export default {
  extends: PageModel,
  components: {
    PageHeader,
    Icon
  },

  data() {
    return {
      // 種別の並び [{ code, name }]（カテゴリーが増えたら estimateFixedAttachmentManager.KINDS に足す）
      kinds: estimateFixedAttachmentManager.KINDS,
      // マスタの一覧
      list: [],
      loading: false,
      // 保存・削除中
      busy: false,
      // ファイルを枠の上まで持ってきている種別
      dragging: '',
      dragDepth: 0
    }
  },

  computed: {
    /**
     * 追加・削除・並べ替えができるか（管理者だけ）
     */
    canEdit() {
      const auth = this.$store.getters.auth
      return !!(auth && auth.adminAuth)
    }
  },

  async created() {
    await this.getList()
  },

  methods: {
    /**
     * 一覧を読む
     * API: estimateFixedAttachment/getList
     */
    async getList() {
      this.loading = true
      try {
        this.list = await estimateFixedAttachmentManager.getList()
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('固定添付マスタ', '一覧を読めませんでした。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 種別の行（並び順）
     * @param kind 種別の名前（KINDS の name）
     */
    rowsOf(kind) {
      return this.list.filter((r) => r.categoryName == kind)
    },

    /**
     * 矢印の列を出すか（管理者で、並べ替える相手がいる＝ 2 行以上。1 行だと空の列になるので出さない）
     * @param kind 種別の名前（KINDS の name）
     */
    canSort(kind) {
      return this.canEdit && this.rowsOf(kind).length > 1
    },

    /**
     * 大きさの表示
     */
    sizeText(bytes) {
      const n = Number(bytes) || 0
      if (n >= 1024 * 1024) {
        return (n / 1024 / 1024).toFixed(1) + ' MB'
      }
      return Math.ceil(n / 1024) + ' KB'
    },

    /**
     * PDF を別タブで開く
     * API: estimateFixedAttachment/file
     */
    async openFile(r) {
      try {
        await estimateFixedAttachmentManager.openFile({ id: r.id }, r.fileName)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('固定添付マスタ', 'PDF を開けませんでした。')
      }
    },

    onDragEnter(kind, event) {
      if (this.busy || !hasFiles(event)) {
        return
      }
      this.dragDepth++
      this.dragging = kind
    },

    onDragLeave() {
      if (this.dragDepth > 0) {
        this.dragDepth--
      }
      if (this.dragDepth === 0) {
        this.dragging = ''
      }
    },

    onDrop(kind, event) {
      this.dragDepth = 0
      this.dragging = ''
      if (this.busy || !hasFiles(event)) {
        return
      }
      this.add(kind, Array.from(event.dataTransfer.files))
    },

    pick(kind, event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (files.length) {
        this.add(kind, files)
      }
    },

    /**
     * PDF を追加する（ドロップでも選択でもここ。PDF 以外が混ざっていれば 1 つも入れない）
     * API: estimateFixedAttachment/save
     */
    async add(kind, files) {
      const bad = files.filter((f) => !/\.pdf$/i.test(f.name))
      if (bad.length) {
        await dialogs.showErrorDialog('PDF だけ置けます', bad.map((f) => '・' + f.name).join('\r\n') + '\r\n\r\nPDF 以外が混ざっているので、1 つも追加していません。')
        return
      }
      // API にはコードと名前を送る（種別の一覧はフロントだけが持つ）
      const k = this.kinds.find((x) => x.name == kind)
      this.busy = true
      try {
        for (const f of files) {
          const row = await estimateFixedAttachmentManager.save(k, f, this.$store.getters.user.id)
          if (row) {
            this.list.push(row)
          }
        }
        logManager.recording(this, logManager.Loglevel.INFO, '固定添付マスタ', '追加', kind + ' に ' + files.map((f) => f.name).join('、') + ' を追加しました。')
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('固定添付マスタ', 'PDF を保存できませんでした。中身が PDF か確かめて、もう一度お試しください。')
        await this.getList()
      } finally {
        this.busy = false
      }
    },

    /**
     * 1 行消す（確認してすぐ保存。S3 のファイルは残るので、控えを持つ見積はそのまま送れる）
     * API: estimateFixedAttachment/delete
     */
    async remove(r) {
      const answer = await dialogs.showConfirmDialog('固定添付の削除', `「${r.fileName}」を ${r.categoryName} から消します。よろしいですか。\r\n※これから作る見積には付かなくなります。すでに控えを持っている見積はそのままです。`)
      if (answer != 'YES') {
        return
      }
      this.busy = true
      try {
        await estimateFixedAttachmentManager.remove(r.id)
        this.list = this.list.filter((x) => x.id != r.id)
        logManager.recording(this, logManager.Loglevel.INFO, '固定添付マスタ', '削除', r.categoryName + ' の ' + r.fileName + ' を削除しました。')
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('固定添付マスタ', '削除できませんでした。もう一度お試しください。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 並びを 1 つ上・下へ（すぐ保存）
     * API: estimateFixedAttachment/saveOrder
     */
    async move(kind, index, dir) {
      const rows = this.rowsOf(kind)
      const to = index + dir
      if (to < 0 || to >= rows.length) {
        return
      }
      const moved = rows[index]
      rows.splice(index, 1)
      rows.splice(to, 0, moved)
      this.busy = true
      try {
        await estimateFixedAttachmentManager.saveOrder(rows.map((r) => r.id), this.$store.getters.user.id)
        // 一覧の並びも同じに（ほかの種別はそのまま）
        this.list = [...this.list.filter((r) => r.categoryName != kind), ...rows]
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('固定添付マスタ', '並びを保存できませんでした。')
        await this.getList()
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.head {
  padding-bottom: 0px !important;
}

.main {
  padding: 16px;
  overflow: auto;
  height: calc(100vh - 95px);
}

.kinds {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 16px;
}

.kind {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fff;
}

.kind-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #1e3a8a;
  margin-bottom: 4px;
}

.rows {
  width: 100%;
  table-layout: fixed;
  font-size: 0.9rem;
}

/* 列幅（ファイル名の列が残りを全部使う） */
.col-no {
  width: 36px;
}

.col-size {
  width: 72px;
}

.col-user {
  width: 104px;
}

.col-arrow {
  width: 76px;
}

.col-remove {
  width: 60px;
}

.file-name {
  overflow-wrap: break-word;
}

.rows th {
  background: #f3f4f6;
  color: #374151;
  font-size: 0.75rem;
  font-weight: bold;
  padding: 4px 6px;
  text-align: left;
}

.rows td {
  padding: 4px 6px;
  border-bottom: 1px solid #e5e7eb;
}

.arrow {
  display: inline-flex;
  padding: 2px 8px;
  margin: 0 1px;
  border-radius: 4px;
  background: #4f46e5;
  color: #fff;
}

.arrow:hover {
  background: #6366f1;
}

.arrow:disabled {
  background: #9ca3af;
}

.remove {
  font-size: 0.75rem;
  color: #b91c1c;
  border: 1px solid #b91c1c;
  border-radius: 4px;
  padding: 2px 8px;
}

.remove:hover {
  background: #fee2e2 !important;
}

.remove:disabled {
  color: #9ca3af;
  border-color: #9ca3af;
}

/* 追加のドロップ枠（登録済み一覧の書類と同じ見た目） */
.doc-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 10px;
  padding: 20px 16px;
  border: 3px dashed #4338ca;
  border-radius: 8px;
  background: #eef2ff;
  color: #3730a3;
  cursor: pointer;
  user-select: none;
}

.doc-drop:hover {
  background: #e0e7ff !important;
}

/* ファイルを枠の上まで持ってきたとき（実線・濃い色） */
.doc-drop.is-dragging {
  border-style: solid;
  background: #c7d2fe;
}

.doc-drop.is-off {
  border-color: #9ca3af;
  background: #f3f4f6;
  color: #6b7280;
  cursor: default;
}

/* 中の要素に dragleave を飛ばさない */
.doc-drop > * {
  pointer-events: none;
}

.doc-drop-icon {
  flex: none;
}

.doc-drop-main {
  font-size: 1rem;
  font-weight: bold;
}

.doc-drop-sub {
  margin-top: 2px;
  font-size: 0.8rem;
}
</style>
